import styled, { keyframes } from "styled-components";
import { motion } from "motion/react";

import { Form } from "../../../components/Form";

import background from "../../../assets/background.png";
import { EditorContent } from "@tiptap/react";
import { SortAscending, SortDescending } from "@phosphor-icons/react";
import { DefaultTheme } from "styled-components";

export type Color = keyof Omit<DefaultTheme["colors"], "background" | "gray">;

const skeletonPulse = keyframes`
  0% {
    opacity: .44;
    transform: translateX(-2px);
  }

  50% {
    opacity: .84;
    transform: translateX(2px);
  }

  100% {
    opacity: .44;
    transform: translateX(-2px);
  }
`;

export const SkeletonLine = styled.div<{
  $width: number;
}>`
  width: ${({ $width }) => $width }%;
  height: ${({ theme }) => theme.spacing.x_small }px;
  border-radius: ${({ theme }) => theme.border_radius.large }px;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.text.default }18,
    ${({ theme }) => theme.colors.text.default }33,
    ${({ theme }) => theme.colors.text.default }18
  );
  animation: ${ skeletonPulse } 1.35s ease-in-out infinite;
`;

export const ChatsMain = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  flex: 1;
  width: 100%;
  min-width: 0;
  font: inherit;
  color: inherit;
  text-align: left;
  min-height: 0;
  gap: 0;
  border-radius: ${({ theme }) => theme.border_radius.medium }px;
  border: 1px solid ${({ theme }) => theme.glass.border };
  background-color: ${({ theme }) => theme.glass.background };
  box-shadow: ${({ theme }) => theme.shadow.card };
  overflow: hidden;

  @media (max-width: 767px) {
    border: none;
    border-radius: 0;
    box-shadow: none;
  }
`;

export const LeftView = styled.div<{
  $hasSelectedChat: boolean;
}>`
  display: flex;
  flex-direction: column;
  flex: 0 0 384px;
  width: 384px;
  min-width: 0;
  min-height: 0;
  background-color: ${({ theme }) => theme.glass.background };
  border-right: 1px solid ${({ theme }) => theme.glass.border };
  backdrop-filter: ${({ theme }) => theme.glass.blur };
  background-clip: padding-box;
  overflow: hidden;

  @media (max-width: 1439px) {
    flex-basis: 340px;
    width: 340px;
  }

  @media (min-width: 1600px) {
    flex-basis: 360px;
    width: 360px;
  }

  @media (max-width: 1100px) {
    flex-basis: 310px;
    width: 310px;
  }

  @media (max-width: 767px) {
    display: ${({ $hasSelectedChat }) => $hasSelectedChat ? "none" : "flex" };
    flex: 1 1 100%;
    width: 100%;
    border-right: none;
  }
`;

export const LeftViewHead = styled.div`
  padding: ${({ theme }) => `${ theme.spacing.xx_medium }px ${ theme.spacing.x_medium }px ${ theme.spacing.xxx_small }px`};

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  & > span {
    min-height: ${({ theme }) => theme.font_size.x_medium }px;
    font-size: ${({ theme }) => theme.font_size.x_medium }px;
    line-height: ${({ theme }) => theme.font_size.x_medium }px;
    font-weight: 700;
    letter-spacing: -.02em;

    color: ${({ theme }) => theme.colors.text.default };
  }
`;

export const ChatListTypes = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.xx_small }px;
  background-color: transparent;
  padding: ${({ theme }) => `${ theme.spacing.x_small }px ${ theme.spacing.x_medium }px ${ theme.spacing.x_medium }px`};
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  & > button {
    flex: 1 0 auto;
    position: relative;
    padding-left: ${({ theme }) => theme.spacing.x_small }px;
    padding-right: ${({ theme }) => theme.spacing.x_small }px;
  }

  & > button > div.badge {
    position: absolute;
    top: -${({ theme }) => theme.spacing.xxx_small }px;
    right: -${({ theme }) => theme.spacing.xx_small }px;
  }

  & > hr.vertical-separator {
    flex: 0 0 1px;
    width: 1px;
    border: none;
    background-color: ${({ theme }) => theme.colors.text.default }44;
  }
`;

export const FormAnimated = styled( motion.create( Form ))`
  padding: 0 ${({ theme }) => theme.spacing.x_medium }px;

  & > .inputs-view {
    padding: 0 0 ${({ theme }) => theme.spacing.xxx_small }px;
  }

  & > .inputs-view > .inputs-view-row > * {
    flex: 1;
  }
`;

export const ChatSearchForm = styled( Form )`
  padding: 0 ${({ theme }) => theme.spacing.x_medium }px ${({ theme }) => theme.spacing.xxx_small }px;

  & > .inputs-view > .search-input-view > svg {
    position: absolute;
    z-index: 1;
    left: ${({ theme }) => theme.spacing.xxx_small }px;
    top: 50%;
    color: ${({ theme }) => theme.colors.gray[ 100 ]};
    pointer-events: none;
    transform: translateY(-50%);
  }

  & > .inputs-view > .search-input-view > input {
    height: 36px;
    padding-left: ${({ theme }) => theme.spacing.xxx_large }px;
    border-color: ${({ theme }) => theme.glass.border };
    background-color: ${({ theme }) => theme.glass.elevated };
  }
`;

export const ChatListHeadActions = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.spacing.x_small }px;
  background-color: transparent;

  & > button {
    width: 32px;
    height: 32px;
  }

  & button:hover,
  & button:focus-visible {
    background-color: ${({ theme }) => theme.colors.selected.default };
  }
`;

export const SortAscendingIcon = styled( motion.create( SortAscending ))`
  position: absolute;
`;

export const SortDescendingIcon = styled( motion.create( SortDescending ))`
  position: absolute;
`;

export const ServicesList = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  margin-top: 0;
  background-color: transparent;
`;

export const ServicesScrollBarView = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  flex: 1;
  min-height: 0;
  width: 100%;
  height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-color: ${({ theme }) => theme.colors.secondary.hover } transparent;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.secondary.hover };
    border-radius: 8px;
  }

  padding: 0;
`;

export const ChatItem = styled.button.attrs({
  type: "button",
})`
  position: relative;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  border: none;
  border-left: 3px solid transparent;
  border-bottom: 1px solid ${({ theme }) => theme.glass.border };
  border-radius: 0;
  background-color: transparent;
  width: 100%;
  min-width: 0;
  gap: ${({ theme }) => theme.spacing.x_small }px;
  padding: ${({ theme }) => `${ theme.spacing.x_medium }px ${ theme.spacing.x_medium }px ${ theme.spacing.x_medium }px ${ theme.spacing.xxx_small }px`};
  transition: background-color .16s ease, border-color .16s ease, transform .16s ease;

  &:hover,
  &:focus-visible {
    background-color: ${({ theme }) => theme.colors.text.default }12;
    outline: none;
  }

  &:focus-visible {
    box-shadow: inset 0 0 0 2px ${({ theme }) => theme.palette.primaryHover };
  }

  &.selected {
    border-left-color: ${({ theme }) => theme.palette.primary };
    background-color: ${({ theme }) => theme.colors.selected.default };
  }

  &.unread:not(.selected) {
    background-color: ${({ theme }) => theme.colors.info.default }0D;
  }

  &.unread > div.contact-view-info > div.chat-content > strong {
    color: ${({ theme }) => theme.colors.text.hover };
    font-weight: 800;
  }

  &.search-occurrence {
    background-color: ${({ theme }) => theme.colors.info.default }0F;
  }

  & > div.badge {
    position: absolute;
    top: ${({ theme }) => theme.spacing.xx_small }px;
    right: ${({ theme }) => theme.spacing.xxx_small }px;
  }

  & > div.contact-view-info {
    position: relative;
    display: flex;
    flex-direction: row;
    color: ${({ theme }) => theme.colors.text.default };
    transition: all .2s;
    min-width: 0;
    gap: ${({ theme }) => theme.spacing.xxx_small }px;

    & > div.avatar-view {
      position: relative;
      flex: 0 0 auto;
    }

    & > div.avatar-view > span.online-badge {
      position: absolute;
      right: -1px;
      bottom: -1px;
      width: ${({ theme }) => theme.font_size.xx_small }px;
      height: ${({ theme }) => theme.font_size.xx_small }px;
      border-radius: 50%;
      background-color: ${({ theme }) => theme.colors.success.default };
      border: 2px solid ${({ theme }) => theme.glass.elevated };
      box-shadow: ${({ theme }) => theme.shadow.default };
    }

    & > div.chat-content {
      display: flex;
      flex-direction: column;
      flex: 1;
      gap: ${({ theme }) => theme.spacing.xx_small }px;
      text-align: left;

      color: ${({ theme }) => theme.colors.text.default };

      overflow: hidden;

      & > strong, & > span {
        flex: 1;
      }

      & > strong {
        min-height: ${({ theme }) => theme.font_size.xxx_small }px;
        font-size: ${({ theme }) => theme.font_size.xxx_small }px;
        line-height: ${({ theme }) => theme.font_size.xxx_small }px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      & > div.last-message-view {
        display: flex;
        flex-direction: row;
        align-items: flex-end;
        gap: ${({ theme }) => theme.spacing.xx_small }px;

        & > div {
          min-height: ${({ theme }) => theme.font_size.xx_small }px;
          font-size: ${({ theme }) => theme.font_size.xx_small }px;
          line-height: ${({ theme }) => theme.font_size.xx_small }px;

          display: inline-block;
          vertical-align: bottom;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        }

        & > div > .content-message-icon {
          margin-bottom: -4px;
        }

        & > div > span.content-message {
          margin-left: ${({ theme }) => theme.spacing.x_small }px;
        }

        & > div > span.occurrence-message {
          margin-left: ${({ theme }) => theme.spacing.xx_small }px;
        }

        & mark.search-hit {
          padding: 0 2px;
          border-radius: ${({ theme }) => theme.border_radius.small }px;
          background-color: ${({ theme }) => theme.colors.warning.default }66;
          color: inherit;
        }
      }
    }
  }
`;

export const ChatContentInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xx_small }px;

  & > div {
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
    flex: 1;

    & > strong {
      min-height: ${({ theme }) => theme.font_size.xx_small }px;
      font-size: ${({ theme }) => theme.font_size.xx_small }px;
      line-height: ${({ theme }) => theme.font_size.xx_small }px;
      font-weight: 500;

      color: ${({ theme }) => theme.colors.info.default };
    }

    & > span {
      display: flex;
      text-align: right;
      min-height: ${({ theme }) => theme.font_size.x_small }px;
      font-size: ${({ theme }) => theme.font_size.x_small }px;
      line-height: ${({ theme }) => theme.font_size.x_small }px;
      font-weight: 300;

      color: ${({ theme }) => theme.colors.text.default };
      font-style: italic;
    }
  }

  & > div:last-of-type {
    & > strong {
      min-height: ${({ theme }) => theme.font_size.xx_small }px;
      font-size: ${({ theme }) => theme.font_size.xx_small }px;
      line-height: ${({ theme }) => theme.font_size.xx_small }px;

      color: ${({ theme }) => theme.colors.text.default };
    }

    & > strong:last-of-type {
      min-height: ${({ theme }) => theme.font_size.xx_small }px;
      font-size: ${({ theme }) => theme.font_size.xx_small }px;
      line-height: ${({ theme }) => theme.font_size.xx_small }px;

      color: ${({ theme }) => theme.colors.text.hover };
    }
  }
`;

export const ListEmptyState = styled.div`
  display: flex;
  flex: 1;
  min-height: 180px;
  width: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.x_small }px;
  padding: ${({ theme }) => theme.spacing.xx_medium }px;
  color: ${({ theme }) => theme.colors.gray[ 100 ]};
  text-align: center;

  & > strong {
    color: ${({ theme }) => theme.colors.text.default };
    font-size: ${({ theme }) => theme.font_size.xx_small }px;
  }

  & > span {
    max-width: 240px;
    font-size: ${({ theme }) => theme.font_size.x_small }px;
    line-height: ${({ theme }) => theme.font_size.xxx_small }px;
  }
`;

export const ServiceSkeletonItem = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  padding: ${({ theme }) => `${ theme.spacing.xxx_small }px ${ theme.spacing.x_medium }px`};
  border-bottom: 1px solid ${({ theme }) => theme.colors.text.default }22;
  gap: ${({ theme }) => theme.spacing.x_medium }px;

  &:first-of-type {
    border-top: 1px solid ${({ theme }) => theme.colors.text.default }22;
  }

  & > div.avatar {
    width: 40px;
    min-width: 40px;
    height: 40px;
    border-radius: 40px;
    background: ${({ theme }) => theme.colors.text.default }22;
    animation: ${ skeletonPulse } 1.35s ease-in-out infinite;
  }

  & > div.content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    flex: 1;
    min-width: 0;
    gap: ${({ theme }) => theme.spacing.xx_small }px;
  }
`;

export const RightView = styled.div<{
  $hasSelectedChat: boolean;
}>`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  min-height: 0;
  backdrop-filter: ${({ theme }) => theme.glass.blur };
  background: ${({ theme, $hasSelectedChat }) => $hasSelectedChat
    ? `linear-gradient( ${ theme.glass.background }, ${ theme.glass.background } ), url(${ background })`
    : "transparent"
  };
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  background-clip: padding-box;
  overflow: hidden;

  @media (max-width: 767px) {
    display: ${({ $hasSelectedChat }) => $hasSelectedChat ? "flex" : "none" };
    width: 100%;
    border-radius: 0;
  }
`;

export const ContextPanelBackdrop = styled.button<{
  $open: boolean;
}>`
  display: none;

  @media (max-width: 1439px) {
    position: absolute;
    inset: 0;
    z-index: 29;
    display: ${({ $open }) => $open ? "block" : "none" };
    width: 100%;
    height: 100%;
    padding: 0;
    border: none;
    background-color: ${({ theme }) => theme.colors.background }99;
    cursor: pointer;
  }
`;

export const ContextPanel = styled.aside<{
  $open: boolean;
}>`
  display: flex;
  flex: 0 0 ${({ $open }) => $open ? 280 : 0 }px;
  width: ${({ $open }) => $open ? 280 : 0 }px;
  min-width: 0;
  min-height: 0;
  flex-direction: column;
  overflow: ${({ $open }) => $open ? "hidden auto" : "hidden" };
  border: none;
  border-left: ${({ theme, $open }) => $open ? `1px solid ${ theme.glass.border }` : "none" };
  border-radius: 0;
  background-color: ${({ theme }) => theme.glass.background };
  box-shadow: none;
  backdrop-filter: ${({ theme }) => theme.glass.blur };
  color: ${({ theme }) => theme.colors.text.default };
  opacity: ${({ $open }) => $open ? 1 : 0 };
  pointer-events: ${({ $open }) => $open ? "auto" : "none" };
  transform: ${({ $open }) => $open ? "translateX(0)" : "translateX(12px)" };
  transition: flex-basis .22s ease, width .22s ease, opacity .16s ease, transform .22s ease;

  & > div.context-head {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: ${({ theme }) => theme.spacing.x_small }px;
    padding: ${({ theme }) => theme.spacing.xxx_small }px;
    border-bottom: 1px solid ${({ theme }) => theme.glass.border };
    background-color: ${({ theme }) => theme.glass.elevated };
  }

  & > div.context-head > div {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.x_small }px;
  }

  & > div.context-head > div > span,
  & span.context-section-title {
    color: ${({ theme }) => theme.colors.info.default };
    font-size: ${({ theme }) => theme.font_size.small }px;
    font-weight: 800;
    letter-spacing: .12em;
  }

  & > div.context-head > div > strong {
    overflow: hidden;
    font-size: ${({ theme }) => theme.font_size.xxx_small }px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  & > div.context-profile {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.xx_small }px;
    padding: ${({ theme }) => `${ theme.spacing.xxx_medium }px ${ theme.spacing.x_medium }px`};
    border-bottom: 1px solid ${({ theme }) => theme.glass.border };
    text-align: center;
  }

  & > div.context-profile > div {
    display: flex;
    min-width: 0;
    max-width: 100%;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.x_small }px;
  }

  & > div.context-profile > div > strong,
  & > div.context-profile > div > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  & > div.context-profile > div > strong {
    font-size: ${({ theme }) => theme.font_size.x_medium }px;
  }

  & > div.context-profile > div > span {
    color: ${({ theme }) => theme.colors.gray[ 100 ]};
    font-size: ${({ theme }) => theme.font_size.xx_small }px;
  }

  & span.context-status {
    padding: ${({ theme }) => `${ theme.spacing.x_small }px ${ theme.spacing.xx_small }px`};
    border: 1px solid ${({ theme }) => theme.colors.info.default }55;
    border-radius: ${({ theme }) => theme.border_radius.large }px;
    background-color: ${({ theme }) => theme.colors.info.default }18;
    color: ${({ theme }) => theme.colors.info.default };
    font-size: ${({ theme }) => theme.font_size.x_small }px;
    font-weight: 700;
  }

  & span.context-status.waiting {
    border-color: ${({ theme }) => theme.colors.warning.default }55;
    background-color: ${({ theme }) => theme.colors.warning.default }18;
    color: ${({ theme }) => theme.colors.warning.default };
  }

  & span.context-status.finalized {
    border-color: ${({ theme }) => theme.colors.gray[ 100 ]}55;
    background-color: ${({ theme }) => theme.colors.gray[ 100 ]}18;
    color: ${({ theme }) => theme.colors.gray[ 100 ]};
  }

  & > div.context-section {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.x_small }px;
    padding: ${({ theme }) => theme.spacing.x_medium }px;
    border-bottom: 1px solid ${({ theme }) => theme.glass.border };
  }

  @media (max-width: 1439px) {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    z-index: 30;
    flex-basis: auto;
    width: min(360px, calc(100% - ${({ theme }) => theme.spacing.xxx_large }px));
    border: 1px solid ${({ theme }) => theme.glass.border };
    border-radius: ${({ theme }) => theme.border_radius.medium }px 0 0 ${({ theme }) => theme.border_radius.medium }px;
    box-shadow: ${({ theme }) => theme.shadow.selected };
    opacity: ${({ $open }) => $open ? 1 : 0 };
    transform: ${({ $open }) => $open ? "translateX(0)" : "translateX(100%)" };
  }

  @media (min-width: 1600px) {
    flex-basis: ${({ $open }) => $open ? 320 : 0 }px;
    width: ${({ $open }) => $open ? 320 : 0 }px;
  }

  @media (max-width: 767px) {
    width: min(100%, 360px);
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
`;

export const ContextField = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.x_medium }px;
  padding: ${({ theme }) => `${ theme.spacing.x_small }px 0`};

  & > span,
  & > strong {
    font-size: ${({ theme }) => theme.font_size.x_small }px;
    line-height: ${({ theme }) => theme.font_size.xxx_small }px;
  }

  & > span {
    flex: 0 0 auto;
    color: ${({ theme }) => theme.colors.gray[ 100 ]};
  }

  & > strong {
    min-width: 0;
    overflow-wrap: anywhere;
    text-align: right;
  }
`;

export const ChatViewEmpty = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.x_medium }px;
  background-color: ${({ theme }) => theme.glass.background };
  backdrop-filter: ${({ theme }) => theme.glass.blur };

  & > img {
    width: 96px;
    max-width: 24%;
    opacity: .72;
    filter: saturate(.75);
  }

  & > span {
    min-height: ${({ theme }) => theme.font_size.xxx_small }px;
    font-size: ${({ theme }) => theme.font_size.xxx_small }px;
    line-height: ${({ theme }) => theme.font_size.xxx_small }px;

    color: ${({ theme }) => theme.colors.text.default };
  }
`;

export const ChatView = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  position: relative;
`;

export const ChatTopBar = styled.div`
  position: relative;
  z-index: 4;
  flex: 0 0 auto;
  background-color: ${({ theme }) => theme.glass.elevated };
  backdrop-filter: ${({ theme }) => theme.glass.blur };

  & > div.service-info {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    gap: ${({ theme }) => theme.spacing.xxx_small }px;
    padding: ${({ theme }) => theme.spacing.x_small }px;
    padding-right: ${({ theme }) => theme.spacing.xxx_large }px;
  }

  & > div.contact-view {
    display: flex;
    z-index: 2;
    flex-direction: row;
    align-items: center;
    min-width: 0;
    min-height: 64px;
    padding: ${({ theme }) => `${ theme.spacing.xx_small }px ${ theme.spacing.xxx_small }px`};
    gap: ${({ theme }) => theme.spacing.xxx_small }px;
    border-bottom: 1px solid ${({ theme }) => theme.glass.border };

    & > div.contact-info {
      display: flex;
      flex: 1;
      min-width: 0;
      flex-direction: column;
      gap: ${({ theme }) => theme.spacing.x_small }px;
    }

    & > div.contact-info > strong {
      flex: 1;

      min-height: ${({ theme }) => theme.font_size.xxx_small }px;
      font-size: ${({ theme }) => theme.font_size.xxx_small }px;
      line-height: ${({ theme }) => theme.font_size.xxx_small }px;
      font-weight: 700;
      letter-spacing: -.01em;

      color: ${({ theme }) => theme.colors.text.default };
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    & > div.contact-info > div.contact-meta {
      display: flex;
      min-width: 0;
      flex-direction: row;
      align-items: center;
      gap: ${({ theme }) => theme.spacing.xx_small }px;
      color: ${({ theme }) => theme.colors.gray[ 100 ]};
      font-size: ${({ theme }) => theme.font_size.x_small }px;
      line-height: ${({ theme }) => theme.font_size.x_small }px;
    }

    & > div.contact-info > div.contact-meta > span:first-child {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    & > div.contact-info > div.contact-meta > span.online-status {
      flex: 0 0 auto;
      font-size: ${({ theme }) => theme.font_size.x_small }px;
      line-height: ${({ theme }) => theme.font_size.x_small }px;
      font-weight: 600;
      color: ${({ theme }) => theme.colors.success.default };
      overflow: hidden;
    }

    & > button {
      flex: 0 0 auto;
    }

  }

  @media (max-width: 767px) {
    & > div.contact-view {
      min-height: 60px;
      padding-left: ${({ theme }) => theme.spacing.x_small }px;
      padding-right: ${({ theme }) => theme.spacing.x_small }px;
      gap: ${({ theme }) => theme.spacing.xx_small }px;
    }

  }
`;

export const ServiceInfoTag = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  & > span {
    color: ${({ theme }) => theme.colors.text.default };
  }

  & > span.service-info-label {
    min-height: ${({ theme }) => theme.font_size.x_small }px;
    font-size: ${({ theme }) => theme.font_size.x_small }px;
    line-height: ${({ theme }) => theme.font_size.x_small }px;
    font-weight: 700;

    padding-bottom: ${({ theme }) => theme.spacing.x_small }px;
  }

  & > span.service-info-value {
    min-height: ${({ theme }) => theme.font_size.xx_small }px;
    font-size: ${({ theme }) => theme.font_size.xx_small }px;
    line-height: ${({ theme }) => theme.font_size.xx_small }px;
    font-weight: 500;

    padding-left: ${({ theme }) => theme.spacing.xx_small }px;
  }
`;

export const ChatMenu = styled( motion.div )`
  position: absolute;
  z-index: 1;
  right: ${({ theme }) => theme.spacing.xxx_medium }px;
  display: flex;
  flex-direction: column;
  z-index: 20;
  gap: ${({ theme }) => theme.spacing.x_small }px;
  background-color: ${({ theme }) => theme.glass.elevated };
  border: 1px solid ${({ theme }) => theme.glass.border };
  backdrop-filter: ${({ theme }) => theme.glass.blur };
  padding: ${({ theme }) => `${ theme.spacing.xx_small }px ${ theme.spacing.none }px`};
  box-shadow: ${({ theme }) => theme.shadow.default };
  overflow: hidden;
  border-radius: ${({ theme }) => theme.border_radius.small }px;
`;

export const ChatMenuItem = styled.button<{
  $color?: Color;
}>`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: ${({ theme }) => `${ theme.spacing.xx_small }px ${ theme.spacing.xxx_small }px`};
  cursor: pointer;
  border: none;
  background-color: transparent;
  color: ${({ theme, $color }) => $color ? theme.colors[ $color ].default : theme.colors.text.default };
  transition: all .2s;

  &:hover {
    background-color: ${({ theme, $color = "selected"}) => theme.colors[ $color ].default }44;
    color: ${({ theme, $color = "primary"}) => theme.colors[ $color ].default };
  }

  & > span {
    flex: 1;
    min-height: ${({ theme }) => theme.font_size.xxx_small }px;
    font-size: ${({ theme }) => theme.font_size.xxx_small }px;
    line-height: ${({ theme }) => theme.font_size.xxx_small }px;
    margin-left: ${({ theme }) => theme.spacing.xxx_small }px;
    margin-right: ${({ theme }) => theme.spacing.xxx_medium }px;
    color: ${({ theme }) => theme.colors.text.default };
    text-align: left;
    transition: all .2s;
  }
`;

export const ChatList = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
`;

export const ScrollToBottom = styled( motion.button )`
  border: none;
  position: absolute;
  cursor: pointer;
  left: calc( 50% - 40px );
  background-color: ${({ theme }) => theme.glass.elevated };
  color: ${({ theme }) => theme.colors.text.default };
  box-shadow: ${({ theme }) => theme.shadow.default };
  border: 1px solid ${({ theme }) => theme.glass.border };
  backdrop-filter: ${({ theme }) => theme.glass.blur };
  padding: ${({ theme }) => theme.spacing.xx_small }px;
  padding-bottom: ${({ theme }) => theme.spacing.x_medium }px;
  border-radius: ${({ theme }) => theme.border_radius.large }px ${({ theme }) => theme.border_radius.large }px 0 0;
`;

export const ChatScrollBarView = styled.div`
  display: flex;
  flex-direction: column-reverse;
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow-y: auto;
  overflow-anchor: none;
  overscroll-behavior: contain;
  scrollbar-color: ${({ theme }) => theme.colors.secondary.hover } transparent;
  scrollbar-width: thin;
  padding: ${({ theme }) => `${ theme.spacing.x_small }px 0 ${ theme.spacing.xxx_small }px`};
  padding-inline: max(0px, calc((100% - 960px) / 2));

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.secondary.hover };
    border-radius: 8px;
  }
`;

export const SkeletonMessagesView = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${({ theme }) => theme.spacing.x_small }px;
  padding: ${({ theme }) => `${ theme.spacing.x_medium }px 0`};
`;

export const SkeletonMessageBubble = styled.div<{
  $side: "left" | "right";
  $width: number;
}>`
  display: flex;
  flex-direction: column;
  align-self: ${({ $side }) => $side === "right" ? "flex-end" : "flex-start" };
  width: ${({ $width }) => $width }%;
  max-width: 384px;
  min-width: 168px;
  margin: ${({ theme, $side }) => $side === "right"
    ? `0 ${ theme.spacing.x_medium }px 0 0`
    : `0 0 0 ${ theme.spacing.xxx_large }px`
  };
  padding: ${({ theme }) => theme.spacing.xx_small }px;
  gap: ${({ theme }) => theme.spacing.xx_small }px;
  border-radius: ${({ theme }) => theme.border_radius.medium }px;
  background-color: ${({ theme, $side }) => $side === "right" ? theme.chatBubble.sent : theme.chatBubble.received };

  & > ${ SkeletonLine }.footer {
    align-self: flex-end;
    justify-content: flex-end;
    height: ${({ theme }) => theme.spacing.xx_small }px;
  }
`;

export const DateGroupMessages = styled.div`
  display: flex;
  flex-direction: column-reverse;
  width: 100%;
  gap: ${({ theme }) => theme.spacing.xx_small }px;
  padding: ${({ theme }) => theme.spacing.x_small }px 0;
`;

export const DateGroupTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: ${({ theme }) => theme.spacing.xx_small }px;
  background-color: transparent;
  margin-bottom: ${({ theme }) => theme.spacing.xxx_small }px;

  & > span {
    min-height: ${({ theme }) => theme.font_size.xx_small }px;
    font-size: ${({ theme }) => theme.font_size.xx_small }px;
    line-height: ${({ theme }) => theme.font_size.xx_small }px;
    color: ${({ theme }) => theme.colors.text.default };
    font-weight: 700;
    padding: ${({ theme }) => `${ theme.spacing.x_small }px ${ theme.spacing.xxx_small }px`};
    border: 1px solid ${({ theme }) => theme.glass.border };
    border-radius: ${({ theme }) => theme.border_radius.large }px;
    background-color: ${({ theme }) => theme.glass.elevated };
    box-shadow: ${({ theme }) => theme.shadow.card };
    backdrop-filter: ${({ theme }) => theme.glass.blur };
  }
`;

export const InternalMessageRow = styled.div<{
  $selected?: boolean;
}>`
  display: flex;
  justify-content: center;
  width: 100%;
  padding: ${({ theme }) => `${ theme.spacing.x_small }px ${ theme.spacing.x_medium }px`};
  background-color: ${({ theme, $selected = false }) => $selected ? `${ theme.colors.info.default }1A` : "transparent" };
  box-shadow: ${({ theme, $selected = false }) => $selected ? `inset 0 0 0 2px ${ theme.colors.info.default }44` : "none" };
  transition: background-color .16s ease, box-shadow .16s ease;

  & > div.internal-message-card {
    display: flex;
    flex-direction: column;
    width: min( 80%, 560px );
    gap: ${({ theme }) => theme.spacing.xx_small }px;
    padding: ${({ theme }) => theme.spacing.xx_small }px ${({ theme }) => theme.spacing.xx_small }px;
    border-radius: ${({ theme }) => theme.border_radius.medium }px;
    /* border: 1px solid ${({ theme }) => theme.colors.gray[300] }44; */
    background-color: ${({ theme }) => theme.colors.gray[100] }55;
    color: ${({ theme }) => theme.colors.text.default };
  }

  & div.internal-message-header {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.xx_small }px;
    color: ${({ theme }) => theme.colors.warning.default };
    font-size: ${({ theme }) => theme.font_size.xx_small }px;
    line-height: ${({ theme }) => theme.font_size.xx_small }px;
  }

  & div.internal-message-header > span {
    margin-left: auto;
    color: ${({ theme }) => theme.colors.text.default }AA;
    font-style: italic;
  }

  & div.internal-message-content {
    font-size: ${({ theme }) => theme.font_size.xx_small }px;
    line-height: ${({ theme }) => theme.font_size.x_medium }px;
  }

  & span.internal-message-time {
    align-self: flex-end;
    color: ${({ theme }) => theme.colors.text.default }AA;
    font-size: ${({ theme }) => theme.font_size.x_small }px;
    font-style: italic;
  }
`;

export const MessageLeftRow = styled.div<{
  $show?: boolean;
  $selected?: boolean;
  $isSticker?: boolean;
  $isImageOrVideo?: boolean;
}>`
  display: flex;
  flex-direction: row;
  width: 100%;
  padding: ${({ theme }) => `${ theme.spacing.x_small }px ${ theme.spacing.xxx_medium }px`};
  background-color: ${({ theme, $selected = false }) => $selected ? `${ theme.colors.info.default }1A` : "transparent" };
  box-shadow: ${({ theme, $selected = false }) => $selected ? `inset 4px 0 0 ${ theme.colors.info.default }` : "none" };
  transition: background-color .16s ease, box-shadow .16s ease;

  min-height: ${({ theme }) => theme.font_size.xx_small }px;
  font-size: ${({ theme }) => theme.font_size.xx_small }px;
  line-height: ${({ theme }) => theme.font_size.x_medium }px;
  color: ${({ theme }) => theme.colors.text.default };

  &:hover .message-actions:not(.message-actions-hidden),
  &:focus-within .message-actions:not(.message-actions-hidden) {
    opacity: 1;
    pointer-events: auto;
    transform: translateY(0) translateX(0);
  }

  & > div:first-of-type  {
    display: flex;
    align-items: flex-start;
    width: 32px;
    height: 100%;
    margin-top: ${({ theme }) => - theme.spacing.x_small }px;
  }

  & > div.message-bubble {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    min-width: 10%;
    max-width: min(68%, 720px);
    margin-left: ${({ theme }) => theme.spacing.x_medium }px;
    position: relative;
    transition: filter .16s ease, box-shadow .16s ease;
    filter: ${({ $selected = false }) => $selected ? "brightness(1.08)" : "none" };
    box-shadow: ${({ theme, $selected = false }) => $selected ? `0 0 0 2px ${ theme.colors.info.default }66` : "none" };
    border-top-left-radius: ${({ theme, $show = false }) => $show ? 0 : theme.border_radius.medium }px;
  }

  & > div.message-bubble > div.name-view {
    display: flex;
    width: 100%;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: ${({ theme }) => theme.spacing.xx_small }px;
    padding-bottom: ${({ theme }) => theme.spacing.none }px;
    background-color: ${({ theme }) => theme.chatBubble.received };
    border-top-left-radius: ${({ theme }) => theme.border_radius.medium }px;
    border-top-right-radius: ${({ theme }) => theme.border_radius.medium }px;
    border-bottom-left-radius: ${({ theme, $isSticker = false }) => $isSticker ? theme.border_radius.medium : 0 }px;
    border-bottom-right-radius: ${({ theme, $isSticker = false }) => $isSticker ? theme.border_radius.medium : 0 }px;
  }

  & > div.message-bubble > div.name-view > span.name {
    min-height: ${({ theme }) => theme.font_size.xx_small }px;
    font-size: ${({ theme }) => theme.font_size.xx_small }px;
    line-height: ${({ theme }) => theme.font_size.xx_small }px;
    background-color: ${({ theme }) => theme.chatBubble.received };
    font-weight: 700;

    margin-bottom: ${({ theme }) => theme.spacing.xx_small }px;
  }

  & > div.message-bubble > div.message-content {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 0 ${({ theme }) => theme.spacing.xx_small }px;
    padding-top: ${({ theme, $show = false }) => $show ? theme.spacing.none : theme.spacing.xx_small }px;
    margin: ${({ theme, $isSticker = false }) => $isSticker ? theme.spacing.xx_small : theme.spacing.none }px 0;
    background-color: ${({ theme, $isSticker = false }) => $isSticker ? "transparent" : theme.chatBubble.received };
    border-top-left-radius: ${({ theme, $show = false }) => $show ? 0 : theme.border_radius.medium }px;
    border-top-right-radius: ${({ theme, $show = false }) => $show ? 0 : theme.border_radius.medium }px;
    border-bottom-left-radius: ${({ theme, $isImageOrVideo = false }) => $isImageOrVideo ? theme.border_radius.medium : 0 }px;
    border-bottom-right-radius: ${({ theme, $isImageOrVideo = false }) => $isImageOrVideo ? theme.border_radius.medium : 0 }px;
  }

  & > div.message-bubble > div.footer {
    position: ${({ $isImageOrVideo = false }) => $isImageOrVideo ? "absolute" : "relative"};
    bottom: ${({ theme, $isImageOrVideo = false }) => $isImageOrVideo ? theme.spacing.xxx_small + "px" : "auto" };
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    justify-content: flex-end;
    gap: ${({ theme }) => theme.spacing.xx_small }px;
    width: ${({ $isSticker = false }) => $isSticker ? "auto" : "100%" };
    padding: ${({ theme }) => theme.spacing.xx_small }px;
    padding-top: ${({ theme }) => theme.spacing.xx_small }px;
    padding-right: ${({ theme, $isImageOrVideo = false }) => $isImageOrVideo ? theme.spacing.x_medium : theme.spacing.xx_small }px;
    background-color: ${({ theme, $isImageOrVideo = false }) => $isImageOrVideo ? "transparent" : theme.chatBubble.received };
    border-top-left-radius: ${({ theme, $isSticker = false }) => $isSticker ? theme.border_radius.medium : 0 }px;
    border-top-right-radius: ${({ theme, $isSticker = false }) => $isSticker ? theme.border_radius.medium : 0 }px;
    border-bottom-left-radius: ${({ theme }) => theme.border_radius.medium }px;
    border-bottom-right-radius: ${({ theme }) => theme.border_radius.medium }px;
  }

  & > div.message-bubble > div.footer > span.mutation,
  & > div.message-bubble > div.footer > span.time {
    min-height: ${({ theme }) => theme.font_size.x_small }px;
    font-size: ${({ theme }) => theme.font_size.x_small }px;
    line-height: ${({ theme }) => theme.font_size.x_small }px;
    font-weight: 300;
    font-style: italic;
  }

  & > div.message-bubble > div.footer > span.mutation {
    text-align: left;
    color: ${({ theme }) => theme.colors.text.default }AA;
  }

  & > div.message-bubble > div.footer > span.time {
    text-align: right;
    margin-left: auto;
    color: ${({ theme, $isImageOrVideo = false }) => $isImageOrVideo ? theme.colors.white.default : theme.colors.text.default };
  }

  ${({ theme, $show = false }) =>
    $show
      ? `
          & > div.message-bubble::after {
            content: "";
            position: absolute;
            left: -8px;
            top: 0;
            width: 0;
            height: 0;
            border-top: 12px solid ${ theme.chatBubble.received };
            border-left: 12px solid transparent;
            border-bottom: 0 solid transparent;
          }
      `
      : ""
  }

  @media (max-width: 767px) {
    padding-left: ${({ theme }) => theme.spacing.xx_small }px;
    padding-right: ${({ theme }) => theme.spacing.xxx_small }px;

    & > div:first-of-type {
      width: 28px;
    }

    & > div.message-bubble {
      max-width: calc(100% - 48px);
      margin-left: ${({ theme }) => theme.spacing.xx_small }px;
    }
  }
`;

export const MessageRightRow = styled.div<{
  $show?: boolean;
  $selected?: boolean;
  $isSticker?: boolean;
  $isImageOrVideo?: boolean;
}>`
  display: flex;
  flex-direction: row-reverse;
  width: 100%;
  padding: ${({ theme }) => `${ theme.spacing.x_small }px ${ theme.spacing.xxx_medium }px`};
  background-color: ${({ theme, $selected = false }) => $selected ? `${ theme.colors.info.default }1A` : "transparent" };
  box-shadow: ${({ theme, $selected = false }) => $selected ? `inset -4px 0 0 ${ theme.colors.info.default }` : "none" };
  transition: background-color .16s ease, box-shadow .16s ease;

  min-height: ${({ theme }) => theme.font_size.xx_small }px;
  font-size: ${({ theme }) => theme.font_size.xx_small }px;
  line-height: ${({ theme }) => theme.font_size.x_medium }px;
  color: ${({ theme }) => theme.colors.text.default };

  &:hover .message-actions:not(.message-actions-hidden),
  &:focus-within .message-actions:not(.message-actions-hidden) {
    opacity: 1;
    pointer-events: auto;
    transform: translateY(0) translateX(0);
  }

  & > div:first-of-type  {
    display: flex;
    align-items: flex-start;
    width: 32px;
    height: 100%;
    margin-top: ${({ theme }) => - theme.spacing.x_small }px;
  }

  & > div.message-bubble {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    min-width: 10%;
    max-width: min(68%, 720px);
    margin-right: ${({ theme }) => theme.spacing.x_medium }px;
    position: relative;
    transition: filter .16s ease, box-shadow .16s ease;
    filter: ${({ $selected = false }) => $selected ? "brightness(1.08)" : "none" };
    box-shadow: ${({ theme, $selected = false }) => $selected ? `0 0 0 2px ${ theme.colors.info.default }66` : "none" };
    border-top-right-radius: ${({ theme, $show = false }) => $show ? 0 : theme.border_radius.medium }px;
  }

  & > div.message-bubble > div.name-view {
    display: flex;
    width: 100%;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: ${({ theme }) => theme.spacing.xx_small }px;
    padding-bottom: ${({ theme }) => theme.spacing.none }px;
    background-color: ${({ theme }) => theme.chatBubble.sent };
    border-top-left-radius: ${({ theme }) => theme.border_radius.medium }px;
    border-top-right-radius: ${({ theme }) => theme.border_radius.medium }px;
    border-bottom-left-radius: ${({ theme, $isSticker = false }) => $isSticker ? theme.border_radius.medium : 0 }px;
    border-bottom-right-radius: ${({ theme, $isSticker = false }) => $isSticker ? theme.border_radius.medium : 0 }px;
  }

  & > div.message-bubble > div.name-view > span.name {
    min-height: ${({ theme }) => theme.font_size.xx_small }px;
    font-size: ${({ theme }) => theme.font_size.xx_small }px;
    line-height: ${({ theme }) => theme.font_size.xx_small }px;
    background-color: ${({ theme }) => theme.chatBubble.sent };
    font-weight: 700;

    margin-bottom: ${({ theme }) => theme.spacing.xx_small }px;
  }

  & > div.message-bubble > div.message-content {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 0 ${({ theme }) => theme.spacing.xx_small }px;
    padding-top: ${({ theme, $show = false }) => $show ? theme.spacing.none : theme.spacing.xx_small }px;
    margin: ${({ theme, $isSticker = false }) => $isSticker ? theme.spacing.xx_small : theme.spacing.none }px 0;
    background-color: ${({ theme, $isSticker = false }) => $isSticker ? "transparent" : theme.chatBubble.sent };
    border-top-left-radius: ${({ theme, $show = false }) => $show ? 0 : theme.border_radius.medium }px;
    border-top-right-radius: ${({ theme, $show = false }) => $show ? 0 : theme.border_radius.medium }px;
    border-bottom-left-radius: ${({ theme, $isImageOrVideo = false }) => $isImageOrVideo ? theme.border_radius.medium : 0 }px;
    border-bottom-right-radius: ${({ theme, $isImageOrVideo = false }) => $isImageOrVideo ? theme.border_radius.medium : 0 }px;
  }

  & > div.message-bubble > div.footer {
    position: ${({ $isImageOrVideo = false }) => $isImageOrVideo ? "absolute" : "relative"};
    bottom: ${({ theme, $isImageOrVideo = false }) => $isImageOrVideo ? theme.spacing.xxx_small + "px" : "auto" };
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    justify-content: flex-end;
    gap: ${({ theme }) => theme.spacing.xx_small }px;
    width: ${({ $isSticker = false }) => $isSticker ? "auto" : "100%" };
    padding: ${({ theme }) => theme.spacing.xx_small }px;
    padding-top: ${({ theme }) => theme.spacing.xx_small }px;
    padding-right: ${({ theme, $isImageOrVideo = false }) => $isImageOrVideo ? theme.spacing.x_medium : theme.spacing.xx_small }px;
    background-color: ${({ theme, $isImageOrVideo = false }) => $isImageOrVideo ? "transparent" : theme.chatBubble.sent };
    border-top-left-radius: ${({ theme, $isSticker = false }) => $isSticker ? theme.border_radius.medium : 0 }px;
    border-top-right-radius: ${({ theme, $isSticker = false }) => $isSticker ? theme.border_radius.medium : 0 }px;
    border-bottom-left-radius: ${({ theme }) => theme.border_radius.medium }px;
    border-bottom-right-radius: ${({ theme }) => theme.border_radius.medium }px;
  }

  & > div.message-bubble > div.footer > span.mutation,
  & > div.message-bubble > div.footer > div.status-view > span.time {
    min-height: ${({ theme }) => theme.font_size.x_small }px;
    font-size: ${({ theme }) => theme.font_size.x_small }px;
    line-height: ${({ theme }) => theme.font_size.x_small }px;
    font-weight: 300;
    font-style: italic;
  }

  & > div.message-bubble > div.footer > span.mutation {
    text-align: left;
  }

  & > div.message-bubble > div.footer > div.status-view {
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    justify-content: flex-end;
    gap: ${({ theme }) => theme.spacing.xx_small }px;
    margin-left: auto;
  }

  & > div.message-bubble > div.footer > div.status-view > svg {
    margin-bottom: -2px;
  }

  & > div.message-bubble > div.footer > div.status-view > span.time {
    text-align: right;
    color: ${({ theme, $isImageOrVideo = false }) => $isImageOrVideo ? theme.colors.white.default : theme.colors.text.default };
  }

  & > div.message-bubble > div.upload-feedback {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.x_small }px;
    min-width: 220px;
  }

  & > div.message-bubble > div.upload-feedback > strong {
    font-size: ${({ theme }) => theme.font_size.xx_small }px;
    line-height: ${({ theme }) => theme.font_size.xx_small }px;
  }

  & > div.message-bubble > div.upload-feedback > span,
  & > div.message-bubble > div.upload-feedback > small {
    color: ${({ theme }) => theme.colors.text.default }CC;
    font-size: ${({ theme }) => theme.font_size.x_small }px;
    line-height: ${({ theme }) => theme.font_size.xx_small }px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  & > div.message-bubble > div.upload-feedback.failed > strong,
  & > div.message-bubble > div.upload-feedback.failed > small {
    color: ${({ theme }) => theme.colors.error.default };
  }

  & > div.message-bubble > div.upload-feedback > div.upload-progress {
    width: 100%;
    height: ${({ theme }) => theme.spacing.x_small }px;
    overflow: hidden;
    border-radius: ${({ theme }) => theme.border_radius.large }px;
    background-color: ${({ theme }) => theme.colors.text.default }24;
  }

  & > div.message-bubble > div.upload-feedback > div.upload-progress > span {
    display: block;
    height: 100%;
    border-radius: inherit;
    background-color: ${({ theme }) => theme.colors.info.default };
    transition: width .2s ease;
  }

  & > div.message-bubble > div.upload-feedback.failed > div.upload-progress > span {
    background-color: ${({ theme }) => theme.colors.error.default };
  }

  & > div.message-bubble > div.upload-feedback > button {
    align-self: flex-start;
    padding: ${({ theme }) => `${ theme.spacing.x_small }px ${ theme.spacing.xx_small }px`};
    border: 1px solid ${({ theme }) => theme.colors.error.default };
    border-radius: ${({ theme }) => theme.border_radius.medium }px;
    background-color: transparent;
    color: ${({ theme }) => theme.colors.error.default };
    cursor: pointer;
    font-size: ${({ theme }) => theme.font_size.x_small }px;
    font-weight: 700;
  }

  ${({ theme, $show = false }) =>
    $show
      ? `
          & > div.message-bubble::after {
            content: "";
            position: absolute;
            right: -8px;
            top: 0;
            width: 0;
            height: 0;
            border-top: 12px solid ${ theme.chatBubble.sent };
            border-right: 12px solid transparent;
            border-bottom: 0 solid transparent;
          }
      `
      : ""
  }

  @media (max-width: 767px) {
    padding-left: ${({ theme }) => theme.spacing.xxx_small }px;
    padding-right: ${({ theme }) => theme.spacing.xx_small }px;

    & > div:first-of-type {
      width: 28px;
    }

    & > div.message-bubble {
      max-width: calc(100% - 48px);
      margin-right: ${({ theme }) => theme.spacing.xx_small }px;
    }
  }
`;

export const MessageActions = styled.div<{
  $side: "left" | "right";
  $open?: boolean;
}>`
  position: absolute;
  top: -${({ theme }) => theme.spacing.x_small }px;
  bottom: ${({ theme }) => theme.spacing.xxx_small }px;
  ${({ theme, $side }) => $side === "left"
    ? `left: calc(100% + ${ theme.spacing.x_small }px);`
    : `right: calc(100% + ${ theme.spacing.x_small }px);`
  }
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  opacity: ${({ $open = false }) => $open ? 1 : 0 };
  pointer-events: ${({ $open = false }) => $open ? "auto" : "none" };
  transform: ${({ $side, $open = false }) => $open ? "translateY(0) translateX(0)" : $side === "left" ? "translateY(8px) translateX(-14px)" : "translateY(8px) translateX(14px)"};
  transition: opacity .18s ease, transform .18s ease;

  & > button.message-action-trigger {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: ${({ theme }) => theme.spacing.x_small }px;
    border: none;
    background-color: transparent;
    color: ${({ theme }) => theme.colors.text.default };
    cursor: pointer;
    transition: color .16s ease, opacity .16s ease;

    &:hover,
    &:focus-visible {
      color: ${({ theme }) => theme.colors.text.hover };
      outline: none;
    }
  }

  & > button.reaction-action-trigger {
    margin-top: auto;
  }

  & > button.menu-action-trigger {
    order: -1;
  }
`;

export const MessageActionMenu = styled.div<{
  $side: "left" | "right";
  $open?: boolean;
  $floatingLeft?: number;
  $floatingTop?: number;
}>`
  ${({ theme, $side, $floatingLeft, $floatingTop }) => $floatingLeft !== undefined && $floatingTop !== undefined
    ? `
        position: fixed;
        left: ${ $floatingLeft }px;
        top: ${ $floatingTop }px;
      `
    : `
        position: absolute;
        top: 0;
        ${ $side === "left"
          ? `left: calc(100% + ${ theme.spacing.xx_small }px);`
          : `right: calc(100% + ${ theme.spacing.xx_small }px);`
        }
      `
  }
  display: flex;
  flex-direction: column;
  z-index: 20;
  gap: ${({ theme }) => theme.spacing.x_small }px;
  min-width: ${({ theme }) => theme.font_size.xxx_large * 8 }px;
  overflow: hidden;
  padding: ${({ theme }) => `${ theme.spacing.xx_small }px ${ theme.spacing.none }px`};
  border-radius: ${({ theme }) => theme.border_radius.small }px;
  border: 1px solid ${({ theme }) => theme.glass.border };
  background-color: ${({ theme }) => theme.glass.elevated };
  box-shadow: ${({ theme }) => theme.shadow.default };
  backdrop-filter: ${({ theme }) => theme.glass.blur };
  opacity: ${({ $open = false, $floatingLeft, $floatingTop }) => $open && $floatingLeft !== undefined && $floatingTop !== undefined ? 1 : 0 };
  pointer-events: ${({ $open = false }) => $open ? "auto" : "none" };
  transform: ${({ $side, $open = false, $floatingLeft, $floatingTop }) => $open && $floatingLeft !== undefined && $floatingTop !== undefined ? "translateX(0)" : $side === "left" ? "translateX(-8px)" : "translateX(8px)"};
  transition: opacity .16s ease, transform .16s ease;
`;

export const MessageActionButton = styled.button.attrs({
  type: "button",
})<{
  $color?: Color;
}>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxx_small }px;
  border: none;
  padding: ${({ theme }) => `${ theme.spacing.xx_small }px ${ theme.spacing.xxx_small }px`};
  background-color: transparent;
  color: ${({ theme, $color }) => $color ? theme.colors[ $color ].default : theme.colors.text.default };
  cursor: pointer;
  text-align: left;
  font-size: ${({ theme }) => theme.font_size.xxx_small }px;
  line-height: ${({ theme }) => theme.font_size.xxx_small }px;
  transition: background-color .16s ease, color .16s ease;

  & > span {
    flex: 1;
    color: ${({ theme }) => theme.colors.text.default };
    transition: color .16s ease;
  }

  &:hover,
  &:focus-visible {
    background-color: ${({ theme, $color = "selected" }) => theme.colors[ $color ].default }44;
    color: ${({ theme, $color = "primary" }) => theme.colors[ $color ].default };
    outline: none;
  }

  &:hover > span,
  &:focus-visible > span {
    color: ${({ theme }) => theme.colors.text.default };
  }
`;

export const ForwardedLabel = styled.div`
  display: flex;
  align-items: center;
  align-self: flex-start;
  gap: 4px;
  margin-bottom: ${({ theme }) => theme.spacing.x_small }px;
  color: ${({ theme }) => theme.colors.text.default }99;
  font-size: ${({ theme }) => theme.font_size.x_small }px;
  font-style: italic;
  line-height: 1;
`;

export const PersistedFailure = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.x_small }px;
  margin-top: ${({ theme }) => theme.spacing.x_small }px;
  padding-top: ${({ theme }) => theme.spacing.x_small }px;
  border-top: 1px solid ${({ theme }) => theme.colors.error.default }55;
  color: ${({ theme }) => theme.colors.error.default };
  font-size: ${({ theme }) => theme.font_size.x_small }px;

  & > strong {
    flex: 1 1 auto;
  }

  & > button {
    border: 1px solid ${({ theme }) => theme.colors.error.default };
    border-radius: ${({ theme }) => theme.border_radius.small }px;
    padding: 4px 8px;
    background: transparent;
    color: ${({ theme }) => theme.colors.error.default };
    cursor: pointer;
    font: inherit;
    font-weight: 700;
  }

  & > button:disabled {
    cursor: wait;
    opacity: .55;
  }
`;

export const MessageReactions = styled.div<{
  $side: "received" | "sent";
}>`
  position: absolute;
  right: ${({ theme }) => theme.spacing.xx_small }px;
  bottom: -${({ theme }) => theme.spacing.xx_medium }px;
  z-index: 1;
  display: flex;
  align-items: center;
  max-width: calc(100% - ${({ theme }) => theme.spacing.x_medium }px);
  padding: ${({ theme }) => `${ theme.spacing.x_small }px ${ theme.spacing.xx_small }px`};
  border-radius: ${({ theme }) => theme.border_radius.large }px;
  border: 1px solid ${({ theme }) => theme.glass.border };
  background-color: ${({ theme }) => theme.glass.elevated };
  box-shadow: ${({ theme }) => theme.shadow.card };
  backdrop-filter: ${({ theme }) => theme.glass.blur };
  color: ${({ theme }) => theme.colors.text.default };
  font-size: ${({ theme }) => theme.font_size.xx_small }px;
  line-height: ${({ theme }) => theme.font_size.xx_small }px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const MessageReactionPicker = styled.div<{
  $side: "left" | "right";
  $open?: boolean;
  $floatingLeft?: number;
  $floatingTop?: number;
}>`
  ${({ theme, $side, $floatingLeft, $floatingTop }) => $floatingLeft !== undefined && $floatingTop !== undefined
    ? `
        position: fixed;
        left: ${ $floatingLeft }px;
        top: ${ $floatingTop }px;
      `
    : `
        position: absolute;
        top: 0;
        ${ $side === "left"
          ? `left: calc(100% + ${ theme.spacing.xx_small }px);`
          : `right: calc(100% + ${ theme.spacing.xx_small }px);`
        }
      `
  }
  z-index: 20;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xx_small }px;
  padding: ${({ theme }) => theme.spacing.xx_small }px;
  border-radius: ${({ theme }) => theme.border_radius.large }px;
  border: 1px solid ${({ theme }) => theme.glass.border };
  background-color: ${({ theme }) => theme.glass.elevated };
  box-shadow: ${({ theme }) => theme.shadow.card };
  backdrop-filter: ${({ theme }) => theme.glass.blur };
  opacity: ${({ $open = false, $floatingLeft, $floatingTop }) => $open && $floatingLeft !== undefined && $floatingTop !== undefined ? 1 : 0 };
  pointer-events: ${({ $open = false }) => $open ? "auto" : "none" };
  transform-origin: ${({ $side }) => $side === "left" ? "left center" : "right center" };
  transform: ${({ $side, $open = false, $floatingLeft, $floatingTop }) => $open && $floatingLeft !== undefined && $floatingTop !== undefined ? "translateX(0) scale(1)" : $side === "left" ? "translateX(-8px) scale(.96)" : "translateX(8px) scale(.96)"};
  transition: opacity .16s ease, transform .16s ease;

  & > button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${({ theme }) => theme.font_size.xxx_large }px;
    height: ${({ theme }) => theme.font_size.xxx_large }px;
    border: none;
    border-radius: ${({ theme }) => theme.border_radius.medium }px;
    background-color: transparent;
    cursor: pointer;
    font-size: ${({ theme }) => theme.font_size.x_medium }px;
    line-height: ${({ theme }) => theme.font_size.x_medium }px;
    transition: background-color .16s ease, transform .16s ease;
  }

  & > button:hover,
  & > button:focus-visible {
    background-color: ${({ theme }) => theme.colors.selected.default };
    outline: none;
    transform: translateY(-1px);
  }
`;

export const AttachmentView = styled( motion.div )`
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  backdrop-filter: ${({ theme }) => theme.glass.blur };
  background-color: ${({ theme }) => theme.glass.background };
  padding-bottom: 72px;

  & > div.header {
    padding: ${({ theme }) => `${ theme.spacing.x_small }px ${ theme.spacing.xxx_small }px`};
    background-color: ${({ theme }) => theme.glass.elevated };
    border-bottom: 1px solid ${({ theme }) => theme.glass.border };
    z-index: 1;
  }

  & > div.body {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    z-index: 0;
  }

  & div.attachment-gallery {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    gap: ${({ theme }) => theme.spacing.x_small }px;
    padding: ${({ theme }) => theme.spacing.x_medium }px;
    padding-bottom: ${({ theme }) => theme.spacing.none }px;
    overflow: hidden;
  }

  & div.attachment-gallery-main {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    min-height: 0;
  }

  & div.attachment-gallery-stage {
    display: flex;
    align-items: center;
    justify-content: center;
    width: min(100%, 960px);
    height: 100%;
    overflow: hidden;
    border-radius: ${({ theme }) => theme.border_radius.medium }px;
    border: 1px solid ${({ theme }) => theme.glass.border };
    background-color: ${({ theme }) => theme.glass.elevated };
    box-shadow: ${({ theme }) => theme.shadow.card };
    color: ${({ theme }) => theme.colors.text.default };
  }

  & div.attachment-gallery-stage > img,
  & div.attachment-gallery-stage > video,
  & div.attachment-gallery-stage > audio,
  & div.attachment-gallery-stage > iframe {
    max-width: 100%;
    max-height: 100%;
  }

  & div.attachment-gallery-stage > video,
  & div.attachment-gallery-stage > iframe {
    width: 100%;
    height: 100%;
  }

  & button.attachment-gallery-arrow {
    position: absolute;
    top: 50%;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${({ theme }) => theme.font_size.xxx_large }px;
    height: ${({ theme }) => theme.font_size.xxx_large }px;
    border: 1px solid ${({ theme }) => theme.glass.border };
    border-radius: 50%;
    background-color: ${({ theme }) => theme.glass.elevated }66;
    color: ${({ theme }) => theme.colors.text.default };
    box-shadow: ${({ theme }) => theme.shadow.default };
    backdrop-filter: ${({ theme }) => theme.glass.blur };
    cursor: pointer;
    transform: translateY(-50%);
    transition: background-color .16s ease, transform .16s ease;
  }

  & button.attachment-gallery-arrow:hover,
  & button.attachment-gallery-arrow:focus-visible {
    background-color: ${({ theme }) => theme.colors.selected.hover };
    outline: none;
    transform: translateY(-50%) scale(1.04);
  }

  & button.attachment-gallery-arrow-left {
    left: ${({ theme }) => theme.spacing.x_medium }px;
  }

  & button.attachment-gallery-arrow-right {
    right: ${({ theme }) => theme.spacing.x_medium }px;
  }

  & div.attachment-gallery-info {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: ${({ theme }) => theme.spacing.x_small }px;
    color: ${({ theme }) => theme.colors.text.default };
  }

  & div.attachment-gallery-info > strong {
    max-width: min(640px, 70%);
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: ${({ theme }) => theme.font_size.xx_small }px;
    line-height: ${({ theme }) => theme.font_size.xx_small }px;
  }

  & div.attachment-gallery-info > span {
    flex: 0 0 auto;
    font-size: ${({ theme }) => theme.font_size.x_small }px;
    line-height: ${({ theme }) => theme.font_size.x_small }px;
    color: ${({ theme }) => theme.colors.text.default }BB;
  }

  & div.attachment-gallery-thumbnails {
    display: flex;
    flex-direction: row;
    gap: ${({ theme }) => theme.spacing.xxx_small }px;
    min-height: 64px;
    overflow-x: auto;
    overflow-y: hidden;
    padding: ${({ theme }) => theme.spacing.xxx_small }px 0;
  }

  & div.attachment-gallery-thumbnails > div {
    position: relative;
    flex: 0 0 56px;
    width: 56px;
    height: 64px;
    transition: transform .16s ease;
  }

  & button.attachment-gallery-thumbnail-select {
    display: flex;
    width: 100%;
    height: 100%;
    overflow: hidden;
    padding: 0;
    border: 1px solid ${({ theme }) => theme.glass.border };
    border-radius: ${({ theme }) => theme.border_radius.medium }px;
    background-color: ${({ theme }) => theme.glass.elevated }AA;
    color: ${({ theme }) => theme.colors.text.default };
    cursor: pointer;
    transition: border-color .16s ease, background-color .16s ease, transform .16s ease;
  }

  & div.attachment-gallery-thumbnails > div:hover button.attachment-gallery-thumbnail-select,
  & div.attachment-gallery-thumbnails > div:focus-within button.attachment-gallery-thumbnail-select,
  & div.attachment-gallery-thumbnails > div.selected button.attachment-gallery-thumbnail-select {
    border-color: ${({ theme }) => theme.colors.info.default }CC;
    background-color: ${({ theme }) => theme.colors.info.default }1F;
    outline: none;
  }

  & div.attachment-gallery-thumbnails > div.selected {
    transform: translateY(-2px);
  }

  & button.attachment-gallery-thumbnail-remove {
    position: absolute;
    top: 2px;
    right: 2px;
    width: 18px;
    height: 18px;
    padding: 0;
    border: 1px solid ${({ theme }) => theme.glass.border };
    background-color: ${({ theme }) => theme.glass.elevated }CC;
    opacity: 0;
    transition: opacity .16s ease, background-color .16s ease;
  }

  & div.attachment-gallery-thumbnails > div:hover button.attachment-gallery-thumbnail-remove,
  & div.attachment-gallery-thumbnails > div:focus-within button.attachment-gallery-thumbnail-remove {
    opacity: 1;
  }

  & button.attachment-gallery-thumbnail-remove:hover,
  & button.attachment-gallery-thumbnail-remove:focus-visible {
    background-color: ${({ theme }) => theme.colors.error.default }CC;
    color: ${({ theme }) => theme.colors.white.default };
    outline: none;
  }

  & div.attachment-gallery-thumbnail-media {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    overflow: hidden;
    border-radius: inherit;
    background-color: ${({ theme }) => theme.colors.text.default }12;
  }

  & div.attachment-gallery-thumbnail-media > img,
  & div.attachment-gallery-thumbnail-media > video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

`;

export const AttachmentInput = styled.input`
  display: none;
`;

export const StickerPickerCard = styled( motion.div )`
  position: absolute;
  left: ${({ theme }) => theme.spacing.xx_medium * 2 }px;
  bottom: ${({ theme }) => theme.spacing.xx_small }px;
  z-index: 20;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxx_small }px;
  width: min(360px, calc(100% - ${({ theme }) => theme.spacing.xx_medium * 4 }px));
  max-height: 280px;
  overflow: hidden;
  padding: ${({ theme }) => theme.spacing.xx_small }px;
  border-radius: ${({ theme }) => theme.border_radius.medium }px;
  border: 1px solid ${({ theme }) => theme.glass.border };
  background-color: ${({ theme }) => theme.glass.elevated };
  box-shadow: ${({ theme }) => theme.shadow.default };
  backdrop-filter: ${({ theme }) => theme.glass.blur };
  color: ${({ theme }) => theme.colors.text.default };

  & > div.sticker-picker-head {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: ${({ theme }) => theme.spacing.xxx_small }px;
    padding: ${({ theme }) => theme.spacing.x_small }px ${({ theme }) => theme.spacing.xx_small }px;
  }

  & > div.sticker-picker-head > strong {
    min-height: ${({ theme }) => theme.font_size.xx_small }px;
    font-size: ${({ theme }) => theme.font_size.xx_small }px;
    line-height: ${({ theme }) => theme.font_size.xx_small }px;
  }

  & > div.sticker-picker-head > button,
  & button.sticker-remove {
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    background-color: transparent;
    color: ${({ theme }) => theme.colors.text.default };
    cursor: pointer;
  }

  & > div.sticker-picker-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: ${({ theme }) => theme.spacing.xxx_small }px;
    overflow-y: auto;
    padding-right: ${({ theme }) => theme.spacing.xx_small }px;
  }

  & div.sticker-item {
    position: relative;
    aspect-ratio: 1;
  }

  & button.sticker-send {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    aspect-ratio: 1;
    overflow: hidden;
    padding: ${({ theme }) => theme.spacing.xx_small }px;
    border-radius: ${({ theme }) => theme.border_radius.small }px;
    border: 1px solid ${({ theme }) => theme.colors.text.default }22;
    background-color: ${({ theme }) => theme.colors.text.default }0F;
    cursor: pointer;
    transition: border-color .16s ease, background-color .16s ease, transform .16s ease;
  }

  & button.sticker-send:hover,
  & button.sticker-send:focus-visible {
    border-color: ${({ theme }) => theme.colors.info.default }AA;
    background-color: ${({ theme }) => theme.colors.info.default }1A;
    outline: none;
  }

  & button.sticker-send:active {
    transform: scale(.98);
  }

  & button.sticker-send > img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  & button.sticker-remove {
    position: absolute;
    top: 2px;
    right: 2px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.glass.elevated }DD;
    opacity: 0;
    transition: opacity .16s ease;
  }

  & div.sticker-item:hover button.sticker-remove,
  & div.sticker-item:focus-within button.sticker-remove {
    opacity: 1;
  }

  & > span.sticker-empty {
    min-height: ${({ theme }) => theme.font_size.xx_small }px;
    font-size: ${({ theme }) => theme.font_size.xx_small }px;
    line-height: ${({ theme }) => theme.font_size.xx_small }px;
    color: ${({ theme }) => theme.colors.text.default }CC;
  }
`;

export const EmojiPickerCard = styled( StickerPickerCard )`
  width: min(384px, calc(100% - ${({ theme }) => theme.spacing.xx_medium * 4 }px));
  max-height: 260px;

  & > div.emoji-picker-head {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: ${({ theme }) => theme.spacing.xxx_small }px;
    padding: ${({ theme }) => theme.spacing.x_small }px ${({ theme }) => theme.spacing.xx_small }px;
  }

  & > div.emoji-picker-head > strong {
    min-height: ${({ theme }) => theme.font_size.xx_small }px;
    font-size: ${({ theme }) => theme.font_size.xx_small }px;
    line-height: ${({ theme }) => theme.font_size.xx_small }px;
  }

  & > div.emoji-picker-head > button {
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    background-color: transparent;
    color: ${({ theme }) => theme.colors.text.default };
    cursor: pointer;
  }

  & > div.emoji-picker-grid {
    display: grid;
    grid-template-columns: repeat(8, minmax(0, 1fr));
    gap: ${({ theme }) => theme.spacing.xxx_small }px;
    overflow-y: auto;
    padding-right: ${({ theme }) => theme.spacing.xx_small }px;
  }

  & > div.emoji-picker-grid > button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    aspect-ratio: 1;
    border: 1px solid ${({ theme }) => theme.colors.text.default }14;
    border-radius: ${({ theme }) => theme.border_radius.small }px;
    background-color: ${({ theme }) => theme.colors.text.default }0F;
    cursor: pointer;
    font-size: ${({ theme }) => theme.font_size.x_medium }px;
    line-height: ${({ theme }) => theme.font_size.x_medium }px;
    transition: border-color .16s ease, background-color .16s ease, transform .16s ease;
  }

  & > div.emoji-picker-grid > button:hover,
  & > div.emoji-picker-grid > button:focus-visible {
    border-color: ${({ theme }) => theme.colors.info.default }AA;
    background-color: ${({ theme }) => theme.colors.info.default }1A;
    outline: none;
  }

  & > div.emoji-picker-grid > button:active {
    transform: scale(.96);
  }
`;

export const ChatBottomBar = styled.div<{
  $connected: boolean;
  $attachmentPreview?: boolean;
}>`
  position: relative;
  flex: 0 0 auto;
  padding: ${({ theme }) => theme.spacing.xx_small }px ${({ theme }) => theme.spacing.xxx_small }px ${({ theme }) => theme.spacing.xxx_small }px;
  border-top: 1px solid ${({ theme }) => theme.glass.border };
  background: linear-gradient(180deg, ${({ theme }) => theme.glass.background }, ${({ theme }) => theme.glass.elevated });
  backdrop-filter: ${({ theme }) => theme.glass.blur };
  z-index: 21;
  opacity: ${({ $attachmentPreview = false }) => $attachmentPreview ? .9 : 1 };
  padding-inline: max(${({ theme }) => theme.spacing.xxx_small }px, calc((100% - 960px) / 2));

  & > div.chat-bottom-bar {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: ${({ theme }) => theme.spacing.xx_small }px;
    padding: ${({ theme }) => `${theme.spacing.xx_small }px ${theme.spacing.xxx_small }px`};
    background-color: ${({ theme, $connected }) => $connected ? theme.glass.elevated : theme.colors.error.default };
    border: 1px solid ${({ theme }) => theme.glass.border };
    border-radius: ${({ theme }) => theme.border_radius.large * 2 }px;
    backdrop-filter: ${({ theme, $connected }) => $connected ? theme.glass.blur : "none" };
    box-shadow: ${({ theme }) => theme.shadow.default };
    min-height: ${({ theme }) => theme.font_size.xxx_large * 2 }px;

    & > button {
      flex: 0 0 auto;
      align-self: flex-end;
      margin-bottom: ${({ theme }) => theme.spacing.xx_small }px;
    }

    & > div.composer-main {
      display: flex;
      flex-direction: column;
      flex: 1 1 auto;
      min-width: 0;
      width: 100%;
    }

    & > div.composer-main > div.internal-composer-banner-shell {
      align-self: flex-start;
      overflow: hidden;
    }

    & > div.composer-main span.internal-composer-banner {
      display: block;
      margin-bottom: ${({ theme }) => theme.spacing.xx_small }px;
      padding: ${({ theme }) => `${ theme.spacing.x_small }px ${ theme.spacing.xx_small }px`};
      border-radius: ${({ theme }) => theme.border_radius.large }px;
      background-color: ${({ theme }) => theme.colors.warning.default }22;
      color: ${({ theme }) => theme.colors.warning.default };
      font-size: ${({ theme }) => theme.font_size.x_small }px;
      font-weight: 700;
    }

    & > div.disconnected-actions {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: ${({ theme }) => theme.spacing.x_medium }px;
      width: 100%;
    }

    & > div.disconnected-actions > span.disconnected {
      min-height: ${({ theme }) => theme.font_size.xx_small }px;
      font-size: ${({ theme }) => theme.font_size.xx_small }px;
      line-height: ${({ theme }) => theme.font_size.xx_small }px;
      font-weight: 700;
      color: ${({ theme }) => theme.colors.white.default };
      text-align: center;
    }

    & > span.service-finalized {
      min-height: ${({ theme }) => theme.font_size.xxx_small }px;
      font-size: ${({ theme }) => theme.font_size.xxx_small }px;
      line-height: ${({ theme }) => theme.font_size.xxx_small }px;
      font-weight: 700;
      color: ${({ theme }) => theme.colors.white.default };
      text-align: center;
      width: 100%;
    }
  }

  @media (max-width: 767px) {
    padding: ${({ theme }) => theme.spacing.x_small }px;

    & > div.chat-bottom-bar {
      gap: ${({ theme }) => theme.spacing.x_small }px;
      padding-left: ${({ theme }) => theme.spacing.x_small }px;
      padding-right: ${({ theme }) => theme.spacing.x_small }px;
    }
  }
`;

export const AttachmentActions = styled( motion.div )`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex: 0 0 auto;
  align-self: flex-end;
  gap: ${({ theme }) => theme.spacing.x_small }px;
  margin-bottom: ${({ theme }) => theme.spacing.xx_small }px;
  overflow: hidden;
  transform-origin: left center;

  & > button {
    flex: 0 0 auto;
  }

  @media (max-width: 480px) {
    gap: 0;
  }
`;

export const MultiMessageSelectionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.x_medium }px;
  width: 100%;
  min-width: 0;

  & > div.selection-copy {
    display: flex;
    min-width: 0;
    flex: 1;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.xxx_small }px;
  }

  & > div.selection-copy > strong {
    color: ${({ theme }) => theme.colors.text.default };
    font-size: ${({ theme }) => theme.font_size.xx_small }px;
    line-height: ${({ theme }) => theme.font_size.xx_small }px;
  }

  & > div.selection-copy > span,
  & > div.selection-copy > small {
    color: ${({ theme }) => theme.colors.text.secondary };
    font-size: ${({ theme }) => theme.font_size.x_small }px;
    line-height: ${({ theme }) => theme.font_size.xx_small }px;
  }

  & > div.selection-actions {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.x_small }px;
  }

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;

    & > div.selection-actions {
      justify-content: stretch;
    }

    & > div.selection-actions > button {
      flex: 1;
    }
  }
`;

export const ReplyPreviewCard = styled.div<{
  $composer?: boolean;
  $clickable?: boolean;
  $variant?: "reply" | "edit";
}>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xx_small }px;
  width: 100%;
  min-width: 0;
  margin-bottom: ${({ theme }) => theme.spacing.xx_small }px;
  padding: ${({ theme, $composer = false }) => $composer ? theme.spacing.xxx_small : theme.spacing.xx_small }px;
  border-left: ${({ theme }) => theme.spacing.x_small }px solid ${({ theme, $variant = "reply" }) => $variant === "edit" ? theme.colors.warning.default : theme.colors.info.default };
  border-radius: ${({ theme }) => theme.border_radius.medium }px;
  background-color: ${({ theme, $composer = false }) => $composer ? theme.glass.background : theme.glass.elevated };
  border-top: 1px solid ${({ theme }) => theme.glass.border };
  border-right: 1px solid ${({ theme }) => theme.glass.border };
  border-bottom: 1px solid ${({ theme }) => theme.glass.border };
  box-shadow: ${({ theme, $composer = false }) => $composer ? theme.shadow.card : "none" };
  backdrop-filter: ${({ theme, $composer = false }) => $composer ? theme.glass.blur : "none" };
  overflow: hidden;
  cursor: ${({ $clickable = false }) => $clickable ? "pointer" : "default" };
  transition: border-color .16s ease, background-color .16s ease, transform .16s ease;

  &:hover {
    border-color: ${({ theme, $clickable = false, $variant = "reply" }) => $clickable ? $variant === "edit" ? theme.colors.warning.default : theme.colors.info.default : theme.glass.border };
    background-color: ${({ theme, $clickable = false, $composer = false, $variant = "reply" }) => $clickable ? `${ $variant === "edit" ? theme.colors.warning.default : theme.colors.info.default }1A` : $composer ? theme.glass.background : theme.glass.elevated };
  }

  &:active {
    transform: ${({ $clickable = false }) => $clickable ? "scale(.99)" : "none" };
  }
`;

export const ReplyPreviewMedia = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 ${({ theme }) => theme.font_size.xxx_large * 2 }px;
  width: ${({ theme }) => theme.font_size.xxx_large * 2 }px;
  height: ${({ theme }) => theme.font_size.xxx_large * 2 }px;
  border-radius: ${({ theme }) => theme.border_radius.small }px;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.text.default }14;
  color: ${({ theme }) => theme.colors.text.default };

  & > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const ReplyPreviewContent = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
  gap: ${({ theme }) => theme.spacing.xx_small }px;

  & > strong,
  & > span:not(.reply-preview-action) {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  & > span.reply-preview-action {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.x_small }px;
    min-height: ${({ theme }) => theme.font_size.x_small }px;
    font-size: ${({ theme }) => theme.font_size.x_small }px;
    line-height: ${({ theme }) => theme.font_size.x_small }px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.info.default };

    &.edit {
      color: ${({ theme }) => theme.colors.warning.default };
    }
  }

  & > strong {
    min-height: ${({ theme }) => theme.font_size.xx_small }px;
    font-size: ${({ theme }) => theme.font_size.xx_small }px;
    line-height: ${({ theme }) => theme.font_size.xx_small }px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.text.default };
  }

  & > span {
    min-height: ${({ theme }) => theme.font_size.xx_small }px;
    font-size: ${({ theme }) => theme.font_size.xx_small }px;
    line-height: ${({ theme }) => theme.font_size.xx_small }px;
    color: ${({ theme }) => theme.colors.text.default };
  }
`;

export const ReplyPreviewCloseButton = styled.button.attrs({
  type: "button",
})<{
  $composer?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  padding: ${({ theme }) => theme.spacing.x_small }px;
  border: ${({ theme, $composer = false }) => $composer ? `1px solid ${ theme.glass.border }` : "none" };
  background-color: ${({ theme, $composer = false }) => $composer ? theme.glass.elevated : "transparent" };
  border-radius: ${({ theme }) => theme.border_radius.medium }px;
  color: ${({ theme }) => theme.colors.text.default };
  cursor: pointer;
  transition: color .16s ease;

  &:hover,
  &:focus-visible {
    color: ${({ theme }) => theme.colors.text.hover };
    outline: none;
  }
`;

export const InputTextView = styled( EditorContent )`
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
  overflow: hidden;
  font-size: ${({ theme }) => theme.font_size.xx_small }px;
  background-color: ${({ theme }) => theme.glass.elevated };
  color: ${({ theme }) => theme.colors.text.default };
  cursor: text;
  box-shadow: none;
  border: 1px solid ${({ theme }) => theme.glass.border };
  backdrop-filter: ${({ theme }) => theme.glass.blur };
  border-radius: ${({ theme }) => theme.border_radius.large }px;

  div.ProseMirror {
    width: 100%;
    min-height: ${({ theme }) => theme.font_size.xx_medium + theme.spacing.xxx_small * 2 }px;
    max-height: ${({ theme }) => theme.font_size.xx_medium * 6 + theme.spacing.xxx_small * 2 }px;
    line-height: ${({ theme }) => theme.font_size.xx_medium }px;
    padding: ${({ theme }) => theme.spacing.xxx_small }px;
    overflow-y: auto;
    overflow-wrap: anywhere;
    white-space: pre-wrap;
  }

  div.ProseMirror p {
    margin: 0;
  }

  p.is-editor-empty:first-child::before {
    content: attr( data-placeholder );
    float: left;
    height: 0;
    pointer-events: none;

    font-size: ${({ theme }) => theme.font_size.xx_small }px;

    color: ${({ theme }) => theme.colors.gray[ 200 ]};
  }
`;

export const Toolbar = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xx_small }px;
  gap: ${({ theme }) => theme.spacing.x_small }px;
  background-color: ${({ theme }) => theme.glass.elevated };
  box-shadow: ${({ theme }) => theme.shadow.card };
  border: 1px solid ${({ theme }) => theme.glass.border };
  backdrop-filter: ${({ theme }) => theme.glass.blur };
  border-radius: ${({ theme }) => theme.border_radius.medium }px;
`;
